import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import classes from "./PreviewScalePage.module.css";

const PreviewDownloads = ({ publishedRef, pdfLabel = "Download as PDF", imagesLabel = "Download images" }) => {
  const charts = useSelector(state => state.publishedData);
  const title = useSelector(state => state.title);
  const pending = useRef(null);
  const downloads = useRef(new Map());
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  useEffect(() => {
    const urls = downloads.current;
    return () => {
      pending.current?.abort();
      for (const [url, timer] of urls) { clearTimeout(timer); URL.revokeObjectURL(url); }
      urls.clear();
    };
  }, []);

  const download = async kind => {
    if (pending.current || !charts.length) return;
    const controller = new AbortController();
    pending.current = controller;
    setBusy(kind); setError("");
    // Capture the clicked version. Later changes must not mix different charts.
    const paper = publishedRef.current?.querySelector("[data-published-paper]");
    const snapshot = kind === "pdf" ? paper?.cloneNode(true) : null;
    try {
      const { buildChartPdf, buildImagesZip, exportName } = await import("./chartDownloads");
      const blob = kind === "pdf"
        ? await buildChartPdf(snapshot, title, controller.signal)
        : await buildImagesZip(charts, title, controller.signal);
      if (controller.signal.aborted) return;
      const url = URL.createObjectURL(blob);
      const timer = setTimeout(() => { URL.revokeObjectURL(url); downloads.current.delete(url); }, 60000);
      downloads.current.set(url, timer);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${exportName(title)}.${kind === "pdf" ? "pdf" : "zip"}`;
      document.body.append(link);
      try { link.click(); } finally { link.remove(); }
    } catch {
      if (!controller.signal.aborted) setError("The download could not be created. Please try again. Your charts have been kept.");
    } finally {
      if (pending.current === controller) pending.current = null;
      if (!controller.signal.aborted) setBusy("");
    }
  };
  return <div className={classes.downloads} role="group" aria-label="Chart downloads" aria-busy={!!busy}>
    <button disabled={!!busy || !charts.length} onClick={() => download("pdf")}>{pdfLabel}</button>
    <button disabled={!!busy || !charts.length} onClick={() => download("images")}>{imagesLabel}</button>
    {busy && <p role="status">{busy === "pdf" ? "Preparing PDF…" : "Preparing SVG and PNG images…"}</p>}
    {error && <p role="alert">{error}</p>}
  </div>;
};

export default PreviewDownloads;
